import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { formatPrice } from "../utils/currency";
import {
  TrendingDown, Users, Calendar, XCircle, Star,
  Zap, Loader2, Play, ArrowRight, X, SendHorizontal,
  Lightbulb, RefreshCw, Eye, CheckCircle
} from "lucide-react";

// ─── helpers ────────────────────────────────────────────────────────────────

const DISMISS_KEY = "oportunidades_dismissed";

function loadDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; }
}
function saveDismissed(list) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(list)); } catch {}
}

function impactEuro(count, ticketMedio) {
  if (!count || !ticketMedio) return null;
  return Math.round(count * ticketMedio);
}

const SEGMENT_LABELS = {
  inactive_7d: "Inativos 7-15 dias",
  inactive_15d: "Inativos 15-30 dias",
  inactive_30d: "Inativos 30-60 dias",
  inactive_60d: "Inativos 60+ dias",
  vip: "Clientes VIP",
  all: "Todos os clientes",
};

const DEFAULT_MESSAGES = {
  inactive_15d:
    "Olá {nome}! Sentimos a sua falta em {restaurante}. Volte hoje e aproveite uma oferta especial. Esperamos por si!",
  inactive_30d:
    "Olá {nome}! Já faz algum tempo desde a sua última visita ao {restaurante}. Preparamos novidades — venha conhecer!",
  inactive_60d:
    "Olá {nome}! {restaurante} tem novidades desde a sua última visita. É a nossa última tentativa de o ter de volta — venha ver!",
  vip: "Olá {nome}! Como um dos nossos clientes mais especiais, temos uma oferta exclusiva para si em {restaurante}. Venha descobrir!",
  sales_drop:
    "Olá {nome}! Esta semana temos promoções especiais em {restaurante}. Aproveite hoje e poupe na sua encomenda!",
  slow_day:
    "Olá {nome}! Hoje é dia de aproveitar as nossas promoções em {restaurante}! Faça o seu pedido agora.",
};

// ─── build opportunity cards from API data ───────────────────────────────────

function buildOpportunities(brainStatus, dashStats, segments) {
  const cards = [];
  const recent = brainStatus?.recent || [];
  const ticketMedio = dashStats?.ticket_medio || 0;

  // 1. Clientes inativos 15-30d
  const inact15 = segments?.find(s => s.id === "inactive_15d");
  if (inact15?.count > 0) {
    const impact = impactEuro(inact15.count, ticketMedio);
    cards.push({
      id: "seg_inactive_15d",
      type: "opportunity",
      icon: Users,
      iconColor: "text-amber-500",
      badgeColor: "bg-amber-100 text-amber-700",
      badgeLabel: "Clientes a recuperar",
      title: `${inact15.count} clientes não compram há 15-30 dias`,
      subtitle: "Uma mensagem certa no momento certo pode reativar estas vendas.",
      impactLabel: impact ? `Potencial de recuperação: ~${formatPrice(impact)}` : null,
      segment: "inactive_15d",
      count: inact15.count,
      defaultMessage: DEFAULT_MESSAGES.inactive_15d,
      cta: "Enviar campanha de reativação",
      navigateTo: null,
    });
  }

  const inact30 = segments?.find(s => s.id === "inactive_30d");
  if (inact30?.count > 0) {
    const impact = impactEuro(inact30.count, ticketMedio);
    cards.push({
      id: "seg_inactive_30d",
      type: "warning",
      icon: Users,
      iconColor: "text-orange-500",
      badgeColor: "bg-orange-100 text-orange-700",
      badgeLabel: "Risco de perda",
      title: `${inact30.count} clientes inativos há 30-60 dias`,
      subtitle: "Estes clientes estão prestes a abandonar definitivamente. Actue agora.",
      impactLabel: impact ? `Receita recuperável: ~${formatPrice(impact)}` : null,
      segment: "inactive_30d",
      count: inact30.count,
      defaultMessage: DEFAULT_MESSAGES.inactive_30d,
      cta: "Recuperar antes que seja tarde",
      navigateTo: null,
    });
  }

  // 2. Queda de vendas
  const salesDropNotif = recent.find(n => n.rule === "sales_drop");
  if (salesDropNotif) {
    const drop = salesDropNotif.data?.drop_pct;
    const diff = salesDropNotif.data
      ? Math.round((salesDropNotif.data.prev_total || 0) - (salesDropNotif.data.this_total || 0))
      : null;
    cards.push({
      id: "brain_sales_drop",
      type: "critical",
      icon: TrendingDown,
      iconColor: "text-red-500",
      badgeColor: "bg-red-100 text-red-700",
      badgeLabel: "Alerta de vendas",
      title: drop ? `Vendas caíram ${drop}% esta semana` : "Quebra de faturação detetada",
      subtitle: "O sistema identificou uma queda significativa. Lance uma promoção de recuperação.",
      impactLabel: diff ? `Diferença vs semana anterior: -${formatPrice(diff)}` : null,
      segment: "all",
      count: null,
      defaultMessage: DEFAULT_MESSAGES.sales_drop,
      cta: "Lançar promoção agora",
      navigateTo: null,
    });
  }

  // 3. Dia fraco
  const slowDayNotif = recent.find(n => n.rule === "slow_day");
  if (slowDayNotif) {
    const dayName = slowDayNotif.data?.day_name;
    cards.push({
      id: "brain_slow_day",
      type: "warning",
      icon: Calendar,
      iconColor: "text-orange-400",
      badgeColor: "bg-orange-100 text-orange-700",
      badgeLabel: "Dia historicamente fraco",
      title: dayName ? `${dayName} é o dia mais fraco do seu negócio` : "Hoje é historicamente um dia fraco",
      subtitle: "Estimule a procura com uma campanha rápida e aumente o movimento.",
      impactLabel: null,
      segment: "all",
      count: null,
      defaultMessage: DEFAULT_MESSAGES.slow_day,
      cta: "Estimular vendas hoje",
      navigateTo: null,
    });
  }

  // 4. Spike de cancelamentos
  const cancelNotif = recent.find(n => n.rule === "cancellation_spike");
  if (cancelNotif) {
    const rate = cancelNotif.data?.cancel_rate;
    cards.push({
      id: "brain_cancel_spike",
      type: "critical",
      icon: XCircle,
      iconColor: "text-red-400",
      badgeColor: "bg-red-100 text-red-700",
      badgeLabel: "Cancelamentos em alta",
      title: rate ? `${rate}% dos pedidos estão a ser cancelados` : "Aumento de cancelamentos detetado",
      subtitle: "Reveja os motivos e comunique com os clientes afetados antes de os perder.",
      impactLabel: null,
      segment: null,
      count: null,
      defaultMessage: null,
      cta: "Ver pedidos cancelados",
      navigateTo: "/orders",
    });
  }

  // 5. VIP
  const vipSeg = segments?.find(s => s.id === "vip");
  if (vipSeg?.count > 0) {
    cards.push({
      id: "seg_vip",
      type: "opportunity",
      icon: Star,
      iconColor: "text-purple-500",
      badgeColor: "bg-purple-100 text-purple-700",
      badgeLabel: "Clientes VIP",
      title: `${vipSeg.count} clientes VIP identificados`,
      subtitle: "Os seus melhores clientes merecem atenção exclusiva. Surpreenda-os.",
      impactLabel: null,
      segment: "vip",
      count: vipSeg.count,
      defaultMessage: DEFAULT_MESSAGES.vip,
      cta: "Enviar mensagem exclusiva",
      navigateTo: null,
    });
  }

  // 6. Inativos 60d
  const inact60 = segments?.find(s => s.id === "inactive_60d");
  if (inact60?.count > 0) {
    cards.push({
      id: "seg_inactive_60d",
      type: "warning",
      icon: Users,
      iconColor: "text-gray-400",
      badgeColor: "bg-gray-100 text-gray-600",
      badgeLabel: "Clientes perdidos",
      title: `${inact60.count} clientes ausentes há 60+ dias`,
      subtitle: "Última janela de oportunidade para os recuperar. Não deixe escapar.",
      impactLabel: null,
      segment: "inactive_60d",
      count: inact60.count,
      defaultMessage: DEFAULT_MESSAGES.inactive_60d,
      cta: "Última tentativa de reativação",
      navigateTo: null,
    });
  }

  return cards;
}

// ─── SendCampaignDialog ───────────────────────────────────────────────────────

function SendCampaignDialog({ opportunity, onClose, authAxios }) {
  const [message, setMessage] = useState(opportunity?.defaultMessage || "");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!message.trim()) { toast.error("Escreva uma mensagem"); return; }
    setSending(true);
    try {
      const res = await authAxios.post("/ai-marketing/bulk-send", {
        segment: opportunity.segment,
        message_template: message,
        name: `Oportunidade: ${opportunity.title.slice(0, 40)}`,
      });
      const sentCount = res.data?.sent || res.data?.count || opportunity.count || 0;
      toast.success(sentCount > 0 ? `Campanha enviada para ${sentCount} clientes!` : "Campanha enviada!");
      onClose({ sent: true, segment: opportunity.segment, count: sentCount, title: opportunity.title });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar campanha");
    }
    setSending(false);
  };

  const segmentLabel = SEGMENT_LABELS[opportunity?.segment] || opportunity?.segment;
  const estimatedReach = opportunity?.count;

  return (
    <Dialog open onOpenChange={() => onClose(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase flex items-center gap-2">
            <SendHorizontal className="w-4 h-4 text-[#CDA34F]" />
            Enviar Campanha WhatsApp
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="p-3 rounded-lg bg-muted text-sm">
            <p className="font-medium">{opportunity?.title}</p>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-xs text-muted-foreground">
                Segmento: <strong>{segmentLabel}</strong>
              </p>
              {estimatedReach > 0 && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                  ~{estimatedReach} destinatários
                </span>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-muted-foreground mb-1">Mensagem</p>
            <Textarea
              rows={5}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Escreva a mensagem para os clientes..."
              className="text-sm resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use {"{nome}"} e {"{restaurante}"} para personalizar a mensagem.
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => onClose(null)} className="flex-1">Cancelar</Button>
            <Button
              onClick={send}
              disabled={sending}
              className="flex-1 bg-[#CDA34F] hover:bg-[#b8923e] text-white"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SendHorizontal className="w-4 h-4 mr-1" />}
              {sending ? "A enviar..." : "Enviar Agora"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── OpportunityCard ─────────────────────────────────────────────────────────

const TYPE_STYLES = {
  critical: "border-red-200 bg-red-50/40",
  warning: "border-amber-200 bg-amber-50/40",
  opportunity: "border-emerald-200 bg-emerald-50/40",
};

function OpportunityCard({ opp, onAction, onDismiss, onNavigate }) {
  const Icon = opp.icon;
  return (
    <Card className={`border ${TYPE_STYLES[opp.type] || ""} relative`}>
      <button
        onClick={() => onDismiss(opp.id)}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        title="Ignorar"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-white border flex items-center justify-center shrink-0">
            <Icon className={`w-5 h-5 ${opp.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${opp.badgeColor}`}>
                {opp.badgeLabel}
              </span>
              {opp.count > 0 && (
                <span className="text-xs text-muted-foreground font-medium">
                  {opp.count} clientes
                </span>
              )}
            </div>
            <p className="font-semibold text-sm leading-snug">{opp.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{opp.subtitle}</p>
            {opp.impactLabel && (
              <p className="text-xs font-semibold text-emerald-700 mt-1.5">
                💰 {opp.impactLabel}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          {opp.navigateTo ? (
            <Button size="sm" onClick={() => onNavigate(opp.navigateTo)} className="gap-1 bg-[#CDA34F] hover:bg-[#b8923e] text-white h-8 text-xs">
              <Eye className="w-3.5 h-3.5" />{opp.cta}
            </Button>
          ) : (
            <Button size="sm" onClick={() => onAction(opp)} className="gap-1 bg-[#CDA34F] hover:bg-[#b8923e] text-white h-8 text-xs">
              <Play className="w-3.5 h-3.5" />{opp.cta}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onNavigate("/ai-marketing")} className="gap-1 h-8 text-xs">
            <ArrowRight className="w-3.5 h-3.5" />Ver segmentos
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function OportunidadesPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const axRef = useRef(authAxios);
  axRef.current = authAxios;
  const runBrainTimeoutRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [brainStatus, setBrainStatus] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [segmentsData, setSegmentsData] = useState(null);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [activeDialog, setActiveDialog] = useState(null);
  const [runningBrain, setRunningBrain] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [brainRes, statsRes, segRes] = await Promise.all([
        axRef.current.get("/brain/status").catch(() => ({ data: null })),
        axRef.current.get("/dashboard/stats").catch(() => ({ data: null })),
        axRef.current.get("/ai-marketing/segments").catch(() => ({ data: null })),
      ]);
      setBrainStatus(brainRes.data);
      setDashStats(statsRes.data);
      setSegmentsData(segRes.data?.segments || []);
    } catch {
      toast.error("Erro ao carregar oportunidades");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => () => clearTimeout(runBrainTimeoutRef.current), []);

  const runBrain = async () => {
    setRunningBrain(true);
    try {
      await axRef.current.post("/brain/run");
      toast.success("Análise iniciada! As oportunidades serão atualizadas em breve.");
      runBrainTimeoutRef.current = setTimeout(() => {
        fetchAll();
        setRunningBrain(false);
      }, 5000);
    } catch {
      toast.error("Erro ao iniciar análise. Verifique a ligação.");
      setRunningBrain(false);
    }
  };

  const allOpportunities = buildOpportunities(brainStatus, dashStats, segmentsData);
  const opportunities = allOpportunities.filter(o => !dismissed.includes(o.id));

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    saveDismissed(next);
  };

  const clearDismissed = () => {
    setDismissed([]);
    saveDismissed([]);
  };

  const handleDialogClose = (result) => {
    setActiveDialog(null);
    if (result?.sent) {
      setSentResult(result);
      fetchAll();
    }
  };

  const totalActive = allOpportunities.length;
  const totalDismissed = totalActive - opportunities.length;

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#CDA34F] to-[#a8832e] flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Oportunidades de Crescimento</h1>
              <p className="text-sm text-muted-foreground">
                {totalActive > 0
                  ? `${totalActive} acções identificadas para aumentar as suas vendas`
                  : "O sistema analisa o seu negócio em tempo real para identificar oportunidades"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {totalDismissed > 0 && (
              <Button variant="ghost" size="sm" onClick={clearDismissed} className="text-xs text-muted-foreground gap-1">
                <RefreshCw className="w-3 h-3" />Repor {totalDismissed} ignoradas
              </Button>
            )}
            <Button
              size="sm"
              onClick={runBrain}
              disabled={runningBrain}
              className="bg-[#CDA34F] hover:bg-[#b8923e] text-white gap-1.5"
            >
              {runningBrain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {runningBrain ? "A analisar..." : "Atualizar análise"}
            </Button>
          </div>
        </div>

        {/* Success banner */}
        {sentResult && (
          <Card className="border-emerald-300 bg-emerald-50">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-800 text-sm">
                    Campanha enviada com sucesso!
                    {sentResult.count > 0 && ` — ${sentResult.count} clientes contactados`}
                  </p>
                  <p className="text-xs text-emerald-700">
                    {SEGMENT_LABELS[sentResult.segment] || sentResult.segment} · Acompanhe os resultados em Marketing IA
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 h-8 text-xs gap-1" onClick={() => navigate("/ai-marketing")}>
                  <ArrowRight className="w-3.5 h-3.5" />Ver em Marketing IA
                </Button>
                <button onClick={() => setSentResult(null)} className="text-emerald-400 hover:text-emerald-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary KPIs */}
        {(dashStats || brainStatus) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: "Receita hoje",
                value: dashStats?.today_revenue != null ? formatPrice(dashStats.today_revenue) : "—",
                sub: `${dashStats?.today_orders || 0} pedidos`,
              },
              {
                label: "Ticket médio",
                value: dashStats?.ticket_medio != null ? formatPrice(dashStats.ticket_medio) : "—",
                sub: "por pedido",
              },
              {
                label: "Clientes em risco",
                value: (segmentsData?.find(s => s.id === "inactive_15d")?.count || 0)
                  + (segmentsData?.find(s => s.id === "inactive_30d")?.count || 0),
                sub: "sem pedido há 15-60 dias",
              },
              {
                label: "Ações disponíveis",
                value: totalActive,
                sub: "identificadas pelo Brain",
              },
            ].map(k => (
              <Card key={k.label} className="bg-card border-border/50">
                <CardContent className="py-3 px-4">
                  <p className="text-xs text-muted-foreground">{k.label}</p>
                  <p className="text-2xl font-bold mt-0.5">{k.value}</p>
                  <p className="text-xs text-muted-foreground">{k.sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Opportunities list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#CDA34F]" />
          </div>
        ) : opportunities.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center space-y-3">
              <Zap className="w-14 h-14 mx-auto text-[#CDA34F] opacity-40" />
              <p className="font-semibold text-lg">
                {totalDismissed > 0 ? "Todas as oportunidades foram ignoradas" : "Nenhuma ação identificada neste momento"}
              </p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {totalDismissed > 0
                  ? `Repõe as ${totalDismissed} oportunidade(s) ignorada(s) ou execute nova análise.`
                  : "O sistema analisa o seu negócio de hora em hora. Execute uma análise agora para ver as oportunidades mais recentes."}
              </p>
              <div className="flex gap-2 justify-center pt-2">
                {totalDismissed > 0 && (
                  <Button variant="outline" onClick={clearDismissed} className="gap-1.5">
                    <RefreshCw className="w-4 h-4" />Repor ignoradas
                  </Button>
                )}
                <Button onClick={runBrain} disabled={runningBrain} className="bg-[#CDA34F] hover:bg-[#b8923e] text-white gap-1.5">
                  {runningBrain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Analisar agora
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (() => {
          const criticals = opportunities.filter(o => o.type === "critical");
          const warnings = opportunities.filter(o => o.type === "warning");
          const growth = opportunities.filter(o => o.type === "opportunity");
          const renderGroup = (opps, label, labelColor, Icon) => opps.length === 0 ? null : (
            <div>
              <p className={`text-xs font-bold uppercase mb-2 flex items-center gap-1 ${labelColor}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </p>
              {opps.map(opp => (
                <div key={opp.id} className="mb-2">
                  <OpportunityCard opp={opp} onAction={setActiveDialog} onDismiss={dismiss} onNavigate={navigate} />
                </div>
              ))}
            </div>
          );
          return (
            <div className="space-y-3">
              {renderGroup(criticals, "Ação urgente necessária", "text-red-500", XCircle)}
              {renderGroup(warnings, "Atenção recomendada", "text-amber-500", Calendar)}
              {renderGroup(growth, "Oportunidades de crescimento", "text-emerald-600", Lightbulb)}
            </div>
          );
        })()}

        {/* Last analysis footer */}
        {brainStatus?.last_run && (
          <p className="text-xs text-center text-muted-foreground pt-2">
            Última análise: {new Date(brainStatus.last_run).toLocaleString("pt-PT")} · Análise automática de hora em hora
          </p>
        )}
      </div>

      {/* Campaign dialog */}
      {activeDialog && (
        <SendCampaignDialog
          opportunity={activeDialog}
          onClose={handleDialogClose}
          authAxios={authAxios}
        />
      )}
    </MainLayout>
  );
}
